<template>
  <!-- 🔹 Статичный overlay (не анимируется) -->
  <div 
    class="drawer-overlay"
    v-if="visible"
    @click.self="close"
  ></div>

  <!-- 🔹 Анимируемый drawer (единственный элемент в <transition>) -->
  <transition name="slide-left">
    <div 
      v-if="visible"
      class="drawer-wrapper"
    >
      <div class="drawer-content">

        <!-- 🔹 Header -->
        <div class="drawer-header">
          <div class="header-text">
            <h2 class="drawer-title">Добавить компанию</h2>

            <p class="drawer-subtitle">
              Чтобы создать вакансию, вам необходимо выбрать компанию, которая будет её публиковать.
            </p>
          </div>

          <button class="close-button" @click="close">&#10005;</button>
        </div>

        <!-- 🔹 Body -->
        <div class="drawer-body">
          <div class="choose-company-container">

            <!-- Поиск -->
            <div class="search-actions-row">
              <div class="search-bar">
                <img
                  :src="isLightTheme ? searchLight : searchDark"
                  alt="🔍"
                  class="search-icon"
                />

                <input
                  type="text"
                  v-model="searchQuery"
                  placeholder="Давайте найдем вашу компанию"
                  class="search-input"
                  @input="searchCompanies"
                />

                <button
                  v-if="searchQuery"
                  type="button"
                  class="clear-search-button"
                  @click="clearSearch"
                  aria-label="Очистить поиск"
                >
                  &#10005;
                </button>
              </div>

              <button
                type="button"
                class="create-company-button"
                @click="openCreateCompany"
              >
                <span class="create-company-icon">+</span>

                <span class="create-company-text">
                  Создать компанию
                </span>
              </button>
            </div>

            <!-- Loading -->
            <div v-if="isLoading" class="spinner-container">
              <div class="spinner"></div>
            </div>

            <!-- Результаты -->
            <div
              v-if="!isLoading && searchQuery.trim().length > 0"
              class="company-grid"
            >
              <CompanyToChoose
                v-for="company in searchResults"
                :key="company.company_id"
                :name="company.name"
                :specifics="company.specifics"
                :logoUrl="company.logo_url"
                :companyId="company.company_id"
                :isSelected="selectedCompanyId === company.company_id"
                @companyClicked="toggleCompanySelection"
              />
            </div>

          </div>
        </div>

        <!-- Footer -->
        <div class="drawer-footer">
          <button 
            class="save-btn"
            :disabled="!hasSelection"
            @click="updateUserCompany"
          >
            Сохранить
          </button>
        </div>

      </div>
    </div>
  </transition>
</template>

<script setup>

import { useRouter } from "vue-router";

import { ref, computed, onMounted, onUnmounted } from 'vue';
import CompanyToChoose from "../CompanyToChoose.vue";
import CompanyService from "../../api/CompanyService.js";
import UserService from "../../api/UserService.js";

import searchLight from '../../assets/search_light.png';
import searchDark from '../../assets/search_dark.png';

const router = useRouter();
const isLightTheme = ref(document.documentElement.getAttribute('data-theme') === 'light');
const updateTheme = () => {
  isLightTheme.value = document.documentElement.getAttribute('data-theme') === 'light';
};

const props = defineProps({
  visible: Boolean,
  modelValue: Object,
});

const emit = defineEmits([
  "close",
  "update:modelValue",
  "company-added",
  "startOverlay",
  "stopOverlay"
]);

const close = () => emit("close");
const searchQuery = ref("");
const searchResults = ref([]);
const selectedCompanyId = ref(null);
const isLoading = ref(false);
const isUpdating = ref(false);
const toggleCompanySelection = (companyId) => {
  console.log("👆 toggleCompanySelection →", companyId);
  if (selectedCompanyId.value === companyId) {
    selectedCompanyId.value = null;
  } else {
    selectedCompanyId.value = companyId;
  }
};

const hasSelection = computed(() => selectedCompanyId.value !== null);
let searchTimer;
const searchCompanies = async () => {
  clearTimeout(searchTimer);

  const q = searchQuery.value.trim();
  if (!q) {
    searchResults.value = [];
    return;
  }

  searchTimer = setTimeout(async () => {
    isLoading.value = true;
    try {
      searchResults.value = await CompanyService.searchCompanies(q);
    } catch (e) {
      console.error("❌ Ошибка загрузки компаний:", e);
    } finally {
      isLoading.value = false;
    }
  }, 300);
};

const updateUserCompany = async () => {
  if (!selectedCompanyId.value) return;

  emit("startOverlay");
  isUpdating.value = true;

  const minOverlayTime = new Promise(r => setTimeout(r, 800));

  try {
    
    const selected = searchResults.value.find(
      c => c.company_id === selectedCompanyId.value
    );

    const selectedName = selected?.name || "";

    await Promise.all([
      UserService.updateUserCompany(selectedCompanyId.value, selectedName),
      minOverlayTime,
    ]);

    emit("update:modelValue", {
      companyId: selectedCompanyId.value,
      companyName: selectedName
    });

    emit("company-added", {
      companyId: selectedCompanyId.value,
      companyName: selectedName
    });

  } catch (e) {
    console.error("❌ Ошибка обновления компании:", e);
  } finally {
    isUpdating.value = false;
    emit("stopOverlay");
    emit("close");
  }
};

const clearSearch = () => {
  searchQuery.value = "";
  searchResults.value = [];
  selectedCompanyId.value = null;
};

const openCreateCompany = () => {
    router.push({
        name: "CreateCompany"
    });
};

// ------------------------------
// MOUNT / UNMOUNT
// ------------------------------
onMounted(() => {
  const onEsc = (e) => e.key === 'Escape' && close();
  window.addEventListener('keydown', onEsc);

  const observer = new MutationObserver(updateTheme);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme']
  });

  onUnmounted(() => {
    window.removeEventListener('keydown', onEsc);
    observer.disconnect();
  });
});
</script>

<style scoped>
.drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1000;
}

.drawer-wrapper {
  position: fixed;
  inset: 0;
  display: flex;
  justify-content: flex-start;
  align-items: stretch;
  z-index: 1100;
}

.drawer-content {
  width: 900px;
  height: 100%;
  background-color: var(--drawer-bg, #fff);
  display: flex;
  flex-direction: column;
  box-shadow: 4px 0 20px rgba(0,0,0,0.12);
}

/* HEADER */
.drawer-header {
  display: flex;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
}

.drawer-title {
  flex: 1;
  font-size: 16px;
  font-family: "Eastman-Bold";
}

.close-button {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
}

/* BODY */
.drawer-body {
  flex: 1;
  padding: 30px;
  overflow-y: auto;
}

/* FOOTER */
.drawer-footer {
  padding: 16px 30px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
}

.save-btn {
  background-color: #2887F7;
  color: white;
  padding: 10px 24px;
  border-radius: 24px;
  border: none;
  font-family: "Eastman-Medium", sans-serif;
  font-size: 14px;
  cursor: pointer;
}

.save-btn:disabled {
  background-color: #bbb;
  opacity: 0.6;
  cursor: not-allowed;
}

/* SEARCH BAR */
.search-bar {
  display: flex;
  align-items: center;
  background-color: #f0f0f0;
  border-radius: 30px;
  padding: 10px 15px;
  width: 400px;
  margin-bottom: 20px;
}

.search-icon {
  width: 20px;
  height: 20px;
  margin-right: 10px;
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 14px;
  font-family: "Eastman-Regular", sans-serif;
}

.search-input::placeholder {
  color: #999;
}

/* GRID */
.company-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

/* SPINNER */
.spinner-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(200, 200, 200, 0.3);
  border-top-color: #2887F7;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* DARK THEME FIXES */
html[data-theme="dark"] .drawer-content {
  background-color: #2c2c2c;
}

html[data-theme="dark"] .drawer-header,
html[data-theme="dark"] .drawer-footer {
  border-color: #444;
}

html[data-theme="dark"] .search-bar {
  background-color: #3a3a3a;
  border: 1px solid #555;
}

html[data-theme="dark"] .search-input {
  color: #eee;
}

html[data-theme="dark"] .search-input::placeholder {
  color: #aaa;
}

.header-text {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.drawer-subtitle {
  margin-top: 6px;
  font-size: 13px;
  line-height: 1.4;
  color: #666;
  font-family: "Eastman-Regular", sans-serif;
}

.close-button {
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: #333;
  transition: opacity 0.2s;
}

.close-button:hover {
  opacity: 0.7;
}

html[data-theme="dark"] .close-button {
  color: #ffffff; /* ← белый X */
}

.search-actions-row {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  margin-bottom: 20px;
}

.search-bar {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
  height: 52px;
  margin-bottom: 0;
  padding: 0 16px;
  background-color: #f0f0f0;
  border: 1px solid transparent;
  border-radius: 18px;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background-color 0.2s ease;
}

.search-bar:focus-within {
  border-color: rgba(40, 135, 247, 0.5);
  background-color: #ffffff;
  box-shadow: 0 8px 24px rgba(40, 135, 247, 0.12);
}

.search-icon {
  flex-shrink: 0;
  width: 19px;
  height: 19px;
  margin-right: 10px;
}

.search-input {
  flex: 1;
  min-width: 0;
  background: transparent;
  border: none;
  outline: none;
  font-size: 14px;
  font-family: "Eastman-Regular", sans-serif;
}

.clear-search-button {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  margin-left: 8px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.06);
  color: #777;
  font-size: 12px;
  cursor: pointer;
  transition:
    transform 0.15s ease,
    background-color 0.15s ease;
}

.clear-search-button:hover {
  transform: scale(1.06);
  background-color: rgba(0, 0, 0, 0.10);
}

.create-company-button {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  height: 52px;
  padding: 0 20px;
  border: none;
  border-radius: 18px;
  background: linear-gradient(
    135deg,
    #2887f7 0%,
    #6f67f4 55%,
    #9e47e2 100%
  );
  color: #ffffff;
  font-family: "Eastman-Medium", sans-serif;
  font-size: 14px;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(40, 135, 247, 0.24);
  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease,
    opacity 0.18s ease;
}

.create-company-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 30px rgba(40, 135, 247, 0.30);
}

.create-company-button:active {
  transform: scale(0.98);
}

.create-company-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.18);
  font-size: 18px;
  line-height: 1;
  font-family: Arial, sans-serif;
}

.create-company-text {
  line-height: 1;
}

</style>